import type { PayloadAction } from "@reduxjs/toolkit";
import { get_event } from "../../apis/helper";
import type {
	addSessionResultPayload,
	State,
	StoreEvent,
	StoreSession,
	setEventPayload,
	updateEventPayload,
} from "./events-types";

// Manage SSE connection as a singleton-like reference but with better lifecycle control

export const updateEventHandler = (
	state: State,
	{ payload }: PayloadAction<updateEventPayload>,
) => {
	const existing = state.events[payload.eventId];
	if (!existing) return;

	const updated = {
		...existing,
		...payload.updateData,
	};

	state.events[payload.eventId] = updated as StoreEvent;
};

export const setEventHandler = (
	state: State,
	{ payload }: PayloadAction<setEventPayload>,
) => {
	state.events[payload.eventId] = payload.eventData;
};

export const clearEventHandler = (
	state: State,
	{ payload }: PayloadAction<string>,
) => {
	delete state.events[payload];
};

export const addSessionResultHandler = (
	state: State,
	{ payload }: PayloadAction<addSessionResultPayload>,
) => {
	const event = state.events[payload.eventId];
	if (event?.sessions?.[payload.sessionIdx]) {
		const session = event.sessions[payload.sessionIdx];
		if (!session.result_details) {
			session.result_details = [];
		}
		// Deduplicate: result_idx is the canonical position in the server's array.
		// Skip if we already have an entry at that index (e.g. from a fetchEventThunk
		// that returned data including this result before the SSE event arrived).
		if (payload.resultIdx < session.result_details.length) return;
		session.result_details.push(payload.result);
	}
};

export const addSessionHighlightHandler = (
	state: State,
	{
		payload,
	}: PayloadAction<{
		eventId: string;
		sessionIdx: number;
		highlight: any;
	}>,
) => {
	const event = state.events[payload.eventId];
	if (event?.sessions?.[payload.sessionIdx]) {
		const session = event.sessions[payload.sessionIdx];
		if (!session.highlights) {
			session.highlights = [];
		}
		session.highlights.push(payload.highlight);
	}
};

export const appendSessionHandler = (
	state: State,
	{
		payload,
	}: PayloadAction<{
		eventId: string;
		session: StoreSession;
	}>,
) => {
	const event = state.events[payload.eventId];
	if (event) {
		event.sessions.push(payload.session);
	}
};

export const updateSessionHighlightHandler = (
	state: State,
	{
		payload,
	}: PayloadAction<{
		eventId: string;
		sessionIdx: number;
		highlightIdx: number;
		updates: any;
	}>,
) => {
	const event = state.events[payload.eventId];
	if (
		event?.sessions?.[payload.sessionIdx] &&
		event.sessions[payload.sessionIdx].highlights
	) {
		const highlights = event.sessions[payload.sessionIdx].highlights;
		if (highlights[payload.highlightIdx]) {
			highlights[payload.highlightIdx] = {
				...highlights[payload.highlightIdx],
				...payload.updates,
			};
		}
	}
};

export const eventBookedHandler = (
	state: State,
	{ payload }: PayloadAction<{ eventId: string; at: string; userId: string }>,
) => {
	const event = state.events[payload.eventId];
	if (event) {
		event.booked_at = payload.at;
		// Note: userId from payload is not directly used here, but could be for future features
	}
};

export const eventCheckedInHandler = (
	state: State,
	{ payload }: PayloadAction<{ eventId: string; at: string }>,
) => {
	const event = state.events[payload.eventId];
	if (event) {
		event.checked_in_at = payload.at;
	}
};

const updateSession = (
	state: State,
	eventId: string,
	sessionIdx: number,
	updater: (session: StoreSession) => void,
) => {
	const event = state.events[eventId];
	if (event?.sessions?.[sessionIdx]) {
		updater(event.sessions[sessionIdx]);
	}
};

export const sessionStreamReadyHandler = (
	state: State,
	{
		payload,
	}: PayloadAction<{ eventId: string; sessionIdx: number; streamUrl: string }>,
) => {
	updateSession(state, payload.eventId, payload.sessionIdx, (session) => {
		// The 'stream_url' is not in the official Session schema, so we cast to any to add it.
		// This allows the UI to react to live stream availability.
		(session as any).stream_url = payload.streamUrl;
	});
};

export const sessionSharelinkReadyHandler = (
	state: State,
	{
		payload,
	}: PayloadAction<{ eventId: string; sessionIdx: number; url: string }>,
) => {
	updateSession(state, payload.eventId, payload.sessionIdx, (session) => {
		session.sharelink = payload.url;
	});
};

export const sessionProcessingDoneHandler = (
	state: State,
	{ payload }: PayloadAction<{ eventId: string; sessionIdx: number }>,
) => {
	updateSession(state, payload.eventId, payload.sessionIdx, (session) => {
		session.is_processing = false;
	});
};

export const sessionArchivedHandler = (
	state: State,
	{ payload }: PayloadAction<{ eventId: string; sessionIdx: number }>,
) => {
	updateSession(state, payload.eventId, payload.sessionIdx, (session) => {
		// The 'is_archived' is not in the official Session schema, so we cast to any to add it.
		(session as any).is_archived = true;
		// Archived implies processing is complete — ensure we transition out of endgame_processing
		// even if processing_done was missed.
		session.is_processing = false;
	});
};

export const fetchEventHandler = async (eventId: string) => {
	const data = await get_event(eventId);

	return {
		eventId,
		eventData: data,
	};
};
